import { endOfDay, formatISO, parseISO } from "date-fns";
import { DateRange } from "@common/helpers/date";

/**
 * Calendar-day range expressed as yyyy-MM-dd strings. Unlike Date objects,
 * these are timezone-independent: "2026-07-01" is July 1 wherever it is
 * created, persisted, or rendered.
 */
export interface DateOnlyDateRange {
    start?: string;
    end?: string;
}

export const toDateOnlyDateRange = (
    range: Partial<DateRange>,
): DateOnlyDateRange => ({
    start: range.start
        ? formatISO(range.start, { representation: "date" })
        : undefined,
    end: range.end
        ? formatISO(range.end, { representation: "date" })
        : undefined,
});

/** Local midnight at the start of a calendar day. */
export const toLocalDayStart = (day: string): Date => parseISO(day);

/** The last instant of a calendar day in local time. */
export const toLocalDayEnd = (day: string): Date => endOfDay(parseISO(day));

/**
 * Expands a calendar-day range into local Date bounds (local midnight for
 * start, end of day for end) for consumers whose filter state still uses
 * Date objects. Only call this at the consumer boundary, never persist the
 * result.
 */
export const toLocalDateRange = (
    value: DateOnlyDateRange,
): Partial<DateRange> => ({
    start: value.start ? toLocalDayStart(value.start) : undefined,
    end: value.end ? toLocalDayEnd(value.end) : undefined,
});
